import { ReactNode } from "react";

import { Typography } from "antd";
import { clsx } from "clsx";
import { Link, To } from "react-router-dom";

import { RVRow } from "../RVFlexbox/RVFlexbox";

import styles from "./RVNestedPageHeader.module.scss";

type RVNestedPageHeaderProps = {
    /** Main title for the page. */
    title: string | ReactNode;
    /** Optional subtitle shown beneath the title. */
    subtitle?: string | ReactNode;
    /** URL path for the back link. When omitted, the back link is hidden. */
    backTo?: To;
    /** Custom text label for the back link. Defaults to "Back". */
    backText?: string;
    /** Optional content displayed before the title (e.g. panel toggle). */
    prefix?: ReactNode;
    /** Optional actions displayed on the right of the title row. */
    titleRowActions?: ReactNode;
    /** Alignment for the title so we can bottom aling action when we have sub titles. */
    titleActionAlignment?: "top" | "bottom";
    /** Optional left content for the action row (e.g. search/filters). */
    actionRowLeft?: ReactNode;
    /** Optional right content for the action row (e.g. primary action button). */
    actionRowRight?: ReactNode;
    /** When true, the action row spans the full width instead of using the left/right column layout. */
    fullWidthActionRow?: boolean;
    /** Optional className for additional styling. */
    className?: string;
};

/**
 * RVNestedPageHeader provides a consistent layout for nested page headings with optional
 * back navigation, leading content, and right-aligned actions.
 * Supports both single-row and two-row layouts.
 */
export function RVNestedPageHeader({
    title,
    subtitle,
    backTo,
    backText = "Back",
    prefix,
    titleRowActions,
    actionRowLeft,
    actionRowRight,
    fullWidthActionRow = false,
    className,
    titleActionAlignment = "top",
}: RVNestedPageHeaderProps): JSX.Element {
    const hasTwoRowLayout = actionRowLeft || actionRowRight;

    const renderBackLink = () => {
        if (!backTo) {
            return null;
        }

        const backContent = <span>{`< ${backText}`}</span>;

        return (
            <Link to={backTo as To} className={styles.backLink} aria-label={backText}>
                {backContent}
            </Link>
        );
    };

    return (
        <>
            <RVRow
                justifyContent="space-between"
                alignItems="flex-start"
                className={clsx(styles.newActionRow, className)}
            >
                <div className={styles.leftColumn}>
                    <div className={styles.titleContainer}>
                        {renderBackLink()}
                        <RVRow alignItems="center" gap={1}>
                            {prefix}
                            <Typography.Title level={1} className={styles.title}>
                                {title}
                            </Typography.Title>
                        </RVRow>
                        {subtitle && <Typography.Text className={styles.subtitle}>{subtitle}</Typography.Text>}
                    </div>
                </div>
                <div
                    className={clsx(styles.rightColumn, {
                        [styles.alignBottom]: titleActionAlignment === "bottom",
                    })}
                >
                    {titleRowActions}
                </div>
            </RVRow>
            {hasTwoRowLayout && (
                <RVRow justifyContent="space-between" alignItems="flex-start" className={styles.newActionRow}>
                    {fullWidthActionRow ? (
                        <div className={styles.fullWidthColumn}>{actionRowLeft}</div>
                    ) : (
                        <>
                            <div className={styles.leftColumn}>{actionRowLeft}</div>
                            <div className={styles.rightColumn}>{actionRowRight}</div>
                        </>
                    )}
                </RVRow>
            )}
        </>
    );
}
